import type { SceneObject } from '../../core/SceneObject';
import { seatParts, seatText, type SeatRequest } from '../fit/seat';
import type { SpatialObjectDescriptor } from './spatial-types';

/**
 * The build's seat() requests, run in call order on the objects it created, and the facts as text for its
 * result; '' when it asked for none. Its other parts are measured too, so one that was never seated and
 * has nothing under it is named.
 */
export async function seatBuild(descriptors: readonly SpatialObjectDescriptor[], created: ReadonlyMap<string, SceneObject>,
    resolve: (ref: string) => SceneObject, roots: readonly SceneObject[]): Promise<string> {
    const asked = descriptors.filter(d => d.seat && created.has(d.id)).sort((a, b) => a.seat!.order - b.seat!.order);
    if (!asked.length) return '';
    const objects = (refs: string[] | undefined) => refs?.map(ref => created.get(ref) ?? resolve(ref));
    const requests: SeatRequest[] = [], refused: string[] = [], skipped = new Set<SceneObject>();
    for (const d of asked) {
        try { requests.push({ object: created.get(d.id)!, dir: d.seat!.dir, on: objects(d.seat!.on), ignore: objects(d.seat!.ignore) }); }
        catch (error) {
            refused.push(`! ${d.name}: not seated: ${error instanceof Error ? error.message : String(error)}`);
            skipped.add(created.get(d.id)!);
        }
    }
    try {
        const text = requests.length ? seatText(await seatParts(requests, { among: roots, created: new Set([...created.values()].filter(o => !skipped.has(o))) })) : 'seat: nothing seated.';
        return `\n\n${[text, ...refused].join('\n')}`;
    } catch (error) {
        // Not rolled back: parts seated before the failure keep their new places, and the build stays.
        return `\n\nseat: stopped: ${error instanceof Error ? error.message : String(error)}. Parts seated before it keep their new places.`;
    }
}
